// Native JavaScript validation without external dependencies

// Password validation regex - at least 8 chars, 1 uppercase, 1 number
const PASSWORD_REGEX = /^(?=.*[A-Z])(?=.*\d).{8,}$/;

// Phone number validation regex - international format
const PHONE_REGEX = /^\+?[1-9]\d{1,14}$/;

// Login name validation - alphanumeric, underscore, hyphen
const LOGIN_NAME_REGEX = /^[a-zA-Z0-9_-]+$/;

// Email validation regex
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

// IP address validation regex
const IP_REGEX = /^(\d{1,3}\.){3}\d{1,3}$/;

// SIP extension ID validation - numbers only
const SIP_EXTENSION_REGEX = /^\d+$/;

// Reference code validation - alphanumeric with some special chars
const REFERENCE_CODE_REGEX = /^[a-zA-Z0-9_-]+$/;

/**
 * Validation rule definitions
 */
const VALIDATION_RULES = {
  loginName: {
    required: true,
    minLength: 3,
    maxLength: 50,
    pattern: LOGIN_NAME_REGEX,
    message: 'Login name must be 3-50 characters and contain only letters, numbers, underscores, and hyphens'
  },
  userName: {
    required: true,
    minLength: 2,
    maxLength: 100,
    message: 'Name must be 2-100 characters'
  },
  email: {
    required: true,
    maxLength: 255,
    pattern: EMAIL_REGEX,
    message: 'Please enter a valid email address'
  },
  password: {
    minLength: 8,
    pattern: PASSWORD_REGEX,
    message: 'Password must be at least 8 characters with at least one uppercase letter and one number'
  },
  phoneNumber: {
    pattern: PHONE_REGEX,
    message: 'Please enter a valid phone number'
  },
  adminLevel: {
    required: true,
    min: 0,
    max: 5,
    message: 'Admin level must be between 0 and 5'
  },
  sipExtensionId: {
    maxLength: 20,
    pattern: SIP_EXTENSION_REGEX,
    message: 'SIP Extension ID must contain only numbers'
  },
  sipDeviceId: {
    maxLength: 50,
    message: 'SIP Device ID must be less than 50 characters'
  },
  sipPassword: {
    minLength: 6,
    message: 'SIP Password must be at least 6 characters'
  },
  solidusAgentId: {
    min: 0,
    max: 999999,
    message: 'Solidus Agent ID must be between 0 and 999999'
  },
  brightAgentReferenceCode: {
    maxLength: 100,
    pattern: REFERENCE_CODE_REGEX,
    message: 'Bright Reference Code must be less than 100 characters and contain only letters, numbers, underscores, and hyphens'
  },
  quickSearchAgentReferenceCode: {
    maxLength: 100,
    pattern: REFERENCE_CODE_REGEX,
    message: 'Quick Search Reference Code must be less than 100 characters'
  },
  allowedSimultaneousChatSessions: {
    min: 1,
    max: 20,
    message: 'Chat sessions must be between 1 and 20'
  },
  chatAlias: {
    maxLength: 50,
    message: 'Chat alias must be less than 50 characters'
  },
  allowedIP: {
    message: 'Please enter valid IP addresses separated by commas'
  },
  externalID: {
    maxLength: 100,
    message: 'External ID cannot exceed 100 characters'
  },
  brightAgentReferenceCode: {
    maxLength: 100,
    message: 'Bright reference code cannot exceed 100 characters'
  },
  sipPassword: {
    maxLength: 100,
    message: 'SIP password cannot exceed 100 characters'
  },
  chatName: {
    maxLength: 100,
    message: 'Chat name cannot exceed 100 characters'
  },
  signatureName: {
    maxLength: 100,
    message: 'Signature name cannot exceed 100 characters'
  }
};

/**
 * Validate a single field
 */
function validateField(fieldName, value, rules = VALIDATION_RULES[fieldName]) {
  if (!rules) return { isValid: true, error: null };

  const errors = [];
  const stringValue = String(value || '').trim();

  // Check required
  if (rules.required && (!value || stringValue === '')) {
    errors.push(`${fieldName} is required`);
  }

  // Skip other validations if empty and not required
  if (!stringValue && !rules.required) {
    return { isValid: true, error: null };
  }

  // Check min length
  if (rules.minLength && stringValue.length < rules.minLength) {
    errors.push(`${fieldName} must be at least ${rules.minLength} characters`);
  }

  // Check max length
  if (rules.maxLength && stringValue.length > rules.maxLength) {
    errors.push(`${fieldName} cannot exceed ${rules.maxLength} characters`);
  }

  // Check pattern
  if (rules.pattern && !rules.pattern.test(stringValue)) {
    errors.push(rules.message || `${fieldName} format is invalid`);
  }

  // Check numeric min/max
  if (typeof value === 'number' || !isNaN(value)) {
    const numValue = Number(value);
    if (rules.min !== undefined && numValue < rules.min) {
      errors.push(`${fieldName} must be at least ${rules.min}`);
    }
    if (rules.max !== undefined && numValue > rules.max) {
      errors.push(`${fieldName} cannot exceed ${rules.max}`);
    }
  }

  return {
    isValid: errors.length === 0,
    error: errors.length > 0 ? errors[0] : null
  };
}

/**
 * Validate IP address list
 */
function validateIPList(ipList) {
  if (!ipList) return true;
  
  const ips = ipList.split(';');
  return ips.every(ip => {
    ip = ip.trim();
    if (!ip) return true;
    
    if (!IP_REGEX.test(ip)) return false;
    
    const parts = ip.split('.');
    return parts.every(part => {
      const num = parseInt(part, 10);
      return num >= 0 && num <= 255;
    });
  });
}

/**
 * Validate agent skills array
 */
function validateSkills(skills) {
  if (!Array.isArray(skills)) return { isValid: true, errors: [] };
  
  const errors = [];
  
  skills.forEach((skill, index) => {
    if (!skill.skillId || skill.skillId < 1) {
      errors.push({ field: `skills[${index}].skillId`, message: 'Skill ID is required', code: 'REQUIRED_FIELD' });
    }
    if (!skill.proficiency || skill.proficiency < 1 || skill.proficiency > 5) {
      errors.push({ field: `skills[${index}].proficiency`, message: 'Proficiency level must be between 1 and 5', code: 'INVALID_RANGE' });
    }
    if (!skill.name || !skill.name.trim()) {
      errors.push({ field: `skills[${index}].name`, message: 'Skill name is required', code: 'REQUIRED_FIELD' });
    }
  });
  
  return { isValid: errors.length === 0, errors };
}

/**
 * Main agent validation function
 */
export const validateAgent = (agentData) => {
  const errors = [];
  const isNewAgent = !agentData.id || agentData.id === 0;

  // Validate required fields
  const requiredFields = ['loginName', 'userName', 'email', 'adminLevel'];
  if (isNewAgent) {
    requiredFields.push('password');
  }

  requiredFields.forEach(field => {
    const validation = validateField(field, agentData[field]);
    if (!validation.isValid) {
      errors.push({
        field,
        message: validation.error,
        code: 'VALIDATION_ERROR'
      });
    }
  });

  // Validate optional fields if they have values
  const optionalFields = ['phoneNumber', 'externalID', 'brightAgentReferenceCode', 
                         'sipPassword', 'chatName', 'signatureName'];
  
  optionalFields.forEach(field => {
    if (agentData[field]) {
      const validation = validateField(field, agentData[field]);
      if (!validation.isValid) {
        errors.push({
          field,
          message: validation.error,
          code: 'VALIDATION_ERROR'
        });
      }
    }
  });

  // Validate password confirmation
  if (agentData.password && agentData.confirmPassword) {
    if (agentData.password !== agentData.confirmPassword) {
      errors.push({
        field: 'confirmPassword',
        message: 'Passwords must match',
        code: 'VALIDATION_ERROR'
      });
    }
  }

  // Validate IP addresses
  if (agentData.allowedIp && !validateIPList(agentData.allowedIp)) {
    errors.push({
      field: 'allowedIp',
      message: 'Invalid IP address format',
      code: 'VALIDATION_ERROR'
    });
  }

  // Validate skills
  if (agentData.skills) {
    const skillsValidation = validateSkills(agentData.skills);
    if (!skillsValidation.isValid) {
      errors.push(...skillsValidation.errors);
    }
  }

  return {
    isValid: errors.length === 0,
    data: errors.length === 0 ? agentData : null,
    errors
  };
};

/**
 * Validate agent preferences
 */
export const validateAgentPreferences = (preferencesData) => {
  const errors = [];

  // Validate numeric ranges
  const numericFields = {
    allowedChatAmount: { min: 0, max: 10 },
    chatMessagePreview: { min: 0, max: 1 },
    openExternalSystem: { min: 0, max: 3 },
    interfaceLanguage: { min: 1 },
    spellingLanguage: { min: 1 },
    timezoneID: { min: 1 },
    listSize: { min: 5, max: 100 },
    inboxPreview: { min: 0, max: 2 },
    inboxPreviewTimeDelay: { min: 0, max: 10 },
    darkModeAppearance: { min: 0, max: 2 }
  };

  Object.keys(numericFields).forEach(field => {
    if (preferencesData[field] !== undefined) {
      const value = Number(preferencesData[field]);
      const rules = numericFields[field];
      
      if (rules.min !== undefined && value < rules.min) {
        errors.push({
          field,
          message: `${field} must be at least ${rules.min}`,
          code: 'VALIDATION_ERROR'
        });
      }
      
      if (rules.max !== undefined && value > rules.max) {
        errors.push({
          field,
          message: `${field} cannot exceed ${rules.max}`,
          code: 'VALIDATION_ERROR'
        });
      }
    }
  });

  return {
    isValid: errors.length === 0,
    data: errors.length === 0 ? preferencesData : null,
    errors
  };
};

/**
 * Field-specific validation for real-time validation
 */
export const validateSingleField = (fieldName, value, agentData = {}) => {
  return validateField(fieldName, value);
};

// Check if login name is unique (to be used with API call)
export const validateUniqueLoginName = async (loginName, agentId, apiCall) => {
  if (!loginName || loginName.trim().length < 3) {
    return { isValid: false, error: 'Login name must be at least 3 characters' };
  }
  
  try {
    const response = await apiCall({ loginname: loginName, agentId: agentId || 0 });
    return { 
      isValid: response.success, 
      error: response.success ? null : 'Login name is already taken'
    };
  } catch (error) {
    return { isValid: false, error: 'Unable to verify login name availability' };
  }
};

// Check if email is unique (to be used with API call)
export const validateUniqueEmail = async (email, agentId, apiCall) => {
  const emailValidation = await validateField('email', email);
  if (!emailValidation.isValid) {
    return emailValidation;
  }
  
  try {
    const response = await apiCall({ email, agentId: agentId || 0 });
    return { 
      isValid: response.success, 
      error: response.success ? null : 'Email address is already in use'
    };
  } catch (error) {
    return { isValid: false, error: 'Unable to verify email availability' };
  }
}; 